import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, Loader2, MessagesSquare } from "lucide-react";

export default function CaseMessages({ caseId, height = "h-[360px]" }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = () => {
    api.get(`/cases/${caseId}/messages`).then(({ data }) => {
      setMessages(data);
      api.post(`/cases/${caseId}/messages/read`).catch(() => {});
    }).catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/messages`, { text });
      setMessages((m) => [...m, data]);
    } catch {
      /* ignore */
    } finally {
      setSending(false);
    }
  };

  const roleLabel = (r) => (r === "user" ? "Client" : r === "tech" ? "Specialist" : "Admin");

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl flex flex-col" data-testid="case-messages">
      <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center gap-2">
        <MessagesSquare className="h-4 w-4 text-[#114A31]" />
        <span className="font-heading font-semibold text-[#1C1C1C]">Messages</span>
      </div>
      <div ref={scrollRef} className={`flex-1 overflow-y-auto p-4 space-y-3 ${height}`} data-testid="messages-list">
        {messages.length === 0 ? (
          <p className="text-sm text-[#595D5A] text-center py-8">No messages yet. Start the conversation below.</p>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === user?.user_id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${
                  mine ? "bg-[#114A31] text-white rounded-br-sm" : "bg-[#F9F9F8] border border-[#E5E5E5] text-[#1C1C1C] rounded-bl-sm"
                }`}>
                  <span className={`block text-[10px] uppercase tracking-wide mb-0.5 ${mine ? "text-white/60" : "text-[#595D5A]"}`}>
                    {m.sender_name || roleLabel(m.sender_role)} · {roleLabel(m.sender_role)}
                  </span>
                  {m.text}
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="p-3 border-t border-[#E5E5E5] flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type a message…"
          data-testid="message-input"
          className="flex-1 rounded-full border border-[#E5E5E5] px-4 py-2.5 text-sm focus:outline-none focus:border-[#114A31]"
        />
        <button onClick={send} disabled={sending} data-testid="message-send-btn"
          className="h-10 w-10 rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white flex items-center justify-center shrink-0 disabled:opacity-50">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
